import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { PlusCircle, Edit2, Trash2, Search, Filter, ChevronLeft, ChevronRight } from 'lucide-react';
import Notification from '../../components/Notification/Notification';
import imageCompression from "browser-image-compression";
import { Helmet } from 'react-helmet';
import './ManageProjects.css';

const ManageProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    projectType: '',
    category: ''
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [pageSize, setPageSize] = useState(8);
  const [totalItems, setTotalItems] = useState(0);
  
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: '' // 'success', 'error', 'info'
  });

  const showNotification = (message, type = 'error') => {
    setNotification({
      show: true,
      message,
      type
    });
    
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 5000);
  };

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page,
        limit: pageSize,
        ...filters
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      const response = await axios.get(`https://gladious-constructions.onrender.com/api/properties?${params.toString()}`);
      
      setProjects(response.data.data || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalItems(response.data.totalItems || 0);
      setLoading(false);
    } catch (err) {
      showNotification('Failed to load projects');
      setProjects([]);
      setLoading(false);
    }
  }, [page, pageSize, filters, searchTerm]);

  useEffect(() => {
    fetchProjects();
  }, [page, pageSize, filters, searchTerm, fetchProjects]);

const handleDelete = async (id) => {
  if (window.confirm('Are you sure you want to delete this project?')) {
    try {
      const token = localStorage.getItem("adminToken");

      await axios.delete(`https://gladious-constructions.onrender.com/api/properties/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      showNotification('Project deleted successfully', 'success');
      fetchProjects();
    } catch (err) {
      showNotification('Failed to delete project');
      console.error(err);
    }
  }
};

  const handleEdit = (project) => {
    setSelectedProject(project);
    setIsCreateModalOpen(true);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      projectType: '',
      category: ''
    });
    setSearchTerm('');
    setPage(1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      pageNumbers.push(1);
      
      let startPage = Math.max(2, page - 1);
      let endPage = Math.min(totalPages - 1, page + 1);
      
      if (page <= 3) {
        endPage = Math.min(totalPages - 1, 4);
      }
      
      if (page >= totalPages - 2) {
        startPage = Math.max(2, totalPages - 3);
      }
      
      if (startPage > 2) {
        pageNumbers.push('...');
      }
      
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }
      
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  return (
    <div className="mp-container">
      <Notification 
        show={notification.show} 
        message={notification.message} 
        type={notification.type}
        onClose={() => setNotification(prev => ({ ...prev, show: false }))}
      />
      <Helmet>
            <title>Manage Projects</title>
            <meta name="description" content="Welcome to Gladious – experts in sustainable and modern construction projects." />
          </Helmet>

      <div className="mp-header">
        <button 
          className="mp-create-btn"
          onClick={() => {
            setSelectedProject(null);
            setIsCreateModalOpen(true);
          }}
        >
          <PlusCircle size={18} />
          <span>Add New Project</span>
        </button>
      </div>

      <div className="mp-search-filter-container">
        <div className="mp-search-bar">
          <Search size={18} className="mp-search-icon" />
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
          />
        </div>
        
        <button 
          className="mp-filter-btn"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
        >
          <Filter size={18} />
          <span>Filter</span>
        </button>
      </div>

      {isFilterOpen && (
        <div className="mp-filter-panel">
          <div className="mp-filter-options">
            <div className="mp-filter-group">
              <label>Project Type</label>
              <select 
                name="projectType" 
                value={filters.projectType} 
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>
                <option value="for-sale">For Sale</option>
                <option value="for-rent">For Rent</option>
                <option value="upcoming">Upcoming</option>
                <option value="ongoing">Ongoing</option>
              </select>
            </div>
            
            <div className="mp-filter-group">
              <label>Category</label>
              <select 
                name="category" 
                value={filters.category} 
                onChange={handleFilterChange}
              >
                <option value="">All Categories</option>
                <option value="Residential">Residential</option>
                <option value="Commercial">Commercial</option>
              </select>
            </div>

            <button className="mp-clear-filters" onClick={clearFilters}>
              Clear Filters
            </button>
          </div>
        </div>
      )}
      
      {loading ? (
        <div className="mp-loading-spinner">Loading projects...</div>
      ) : (
        <>
          {projects.length === 0 ? (
            <div className="mp-no-projects">
              <p>No projects found. Try adjusting your filters or add a new project.</p>
            </div>
          ) : (
            <div className="mp-project-grid">
              {projects && projects.map(project => (
                <div key={project._id} className="mp-project-card">
                <div className="mp-project-image">
  {project.images?.length > 0 ? (
    <img
      src={project.images[0]?.replace('/upload/', '/upload/w_400,h_300,c_fill/')}
      onError={(e) => { e.target.src = '/images/logo.png' }}
      alt={project.title}
      loading="lazy"
    />
  ) : (
    <div className="mp-placeholder-image">
      <img src="/images/logo.png" alt="Gladious Placeholder" />
    </div>
  )}
</div>
                  <div className="mp-project-details">
                    <h3>{project.title}</h3>
                    <p className="mp-project-location">
                      {project.location && `${project.location.city || ''}, ${project.location.country || ''}`}
                    </p>
                    <div className="mp-project-info">
                      <span>{project.projectType}</span>
                      <span>{project.category}</span>
                    </div>
                    <p className="mp-project-price">Rs.{project.price ? project.price.toLocaleString() : '0'}</p>
                  </div>
                  
                  <div className="mp-project-actions">
                    <button 
                      className="mp-edit-btn"
                      onClick={() => handleEdit(project)}
                    >
                      <Edit2 size={16} />
                      Edit
                    </button>
                    <button 
                      className="mp-delete-btn"
                      onClick={() => handleDelete(project._id)}
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {totalPages > 1 && (
            <div className="mp-pagination-container">
              <div className="mp-pagination-controls">
                <div className="mp-page-size-selector">
                  <label>Show:</label>
                  <select 
                    value={pageSize}
                    onChange={handlePageSizeChange}
                    className="mp-page-size-select"
                  >
                    <option value={6}>6</option>
                    <option value={9}>9</option>
                    <option value={12}>12</option>
                    <option value={24}>24</option>
                  </select>
                  <span>per page</span>
                </div>
                
                <div className="mp-pagination-info">
                  Showing {projects.length ? (page - 1) * pageSize + 1 : 0} to {Math.min(page * pageSize, totalItems)} of {totalItems} projects
                </div>
              </div>
              
              <div className="mp-pagination">
                <button 
                  className="mp-pagination-btn"
                  disabled={page === 1}
                  onClick={() => setPage(1)}
                  title="First Page"
                >
                  <span>««</span>
                </button>
                
                <button 
                  className="mp-pagination-btn"
                  disabled={page === 1}
                  onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                  title="Previous Page"
                >
                  <ChevronLeft size={18} />
                </button>
                
                <div className="mp-page-numbers">
                  {getPageNumbers().map((num, index) => (
                    num === '...' ? (
                      <span key={`ellipsis-${index}`} className="ellipsis">...</span>
                    ) : (
                      <button
                        key={`page-${num}`}
                        className={`mp-page-number ${page === num ? 'active' : ''}`}
                        onClick={() => setPage(num)}
                        disabled={page === num}
                      >
                        {num}
                      </button>
                    )
                  ))}
                </div>
                
                <button 
                  className="mp-pagination-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                  title="Next Page"
                >
                  <ChevronRight size={18} />
                </button>
                
                <button 
                  className="mp-pagination-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(totalPages)}
                  title="Last Page"
                >
                  <span>»»</span>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {isCreateModalOpen && (
        <ProjectModal 
          project={selectedProject}
          onClose={() => setIsCreateModalOpen(false)}
          onSave={() => {
            setIsCreateModalOpen(false);
            fetchProjects();
          }}
        />
      )}
    </div>
  );
};

const ProjectModal = ({ project, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    projectType: 'for-sale',
    category: 'Residential',
    location: {
      address: '',
      city: '',
      country: ''
    },
    area: {
      value: '',
      unit: 'sqft' // Default unit
    },
    bathrooms: '',
    bedrooms: '',
    phone: '',
    email: '',
    existingImages: [],
    newImages: []
  });

  const [loading, setLoading] = useState(false);
  const [removedImages, setRemovedImages] = useState([]);
  
  const [notification, setNotification] = useState({
    show: false,
    message: '',
    type: ''
  });

  const showNotification = (message, type = 'error') => {
    setNotification({
      show: true,
      message,
      type
    });
    
    setTimeout(() => {
      setNotification(prev => ({ ...prev, show: false }));
    }, 5000);
  };

 useEffect(() => {
  if (project) {
    // Parse the area if it's a string (for backward compatibility)
    let areaData = { value: '', unit: 'sqft' };
    if (project.area) {
      if (typeof project.area === 'string') {
        // Handle old format where area was just a number/string
        areaData = { value: project.area, unit: 'sqft' };
      } else if (typeof project.area === 'object') {
        // Handle new format with value and unit
        areaData = {
          value: project.area.value || '',
          unit: project.area.unit || 'sqft'
        };
      }
    }

    setFormData({
      title: project.title || '',
      description: project.description || '',
      price: project.price || '',
      area: areaData,
      projectType: project.projectType || 'for-sale',
      category: project.category || 'Residential',
      location: {
        address: project.location?.address || '',
        city: project.location?.city || '',
        country: project.location?.country || ''
      },
      bathrooms: project.bathrooms || '',
      bedrooms: project.bathrooms || '',
      phone: project.phone || '',
      email: project.email || '',
      existingImages: project.images || [],
      newImages: []
    });
    setRemovedImages([]);
  }
}, [project]);


  const handleChange = (e) => {
  const { name, value } = e.target;
  
  if (name.includes('.')) {
    const [parent, child] = name.split('.');
    
    if (parent === 'area') {
      setFormData(prev => ({
        ...prev,
        area: {
          ...prev.area,
          [child]: value
        }
      }));
    } else if (parent === 'location') {
      setFormData(prev => ({
        ...prev,
        location: {
          ...prev.location,
          [child]: value
        }
      }));
    }
  } else {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }
};

const handleImageChange = async (e) => {
  if (e.target.files) {
    const maxTotalImages = 5;
    const currentExistingImages = formData.existingImages?.length || 0;
    const maxNewImages = Math.max(0, maxTotalImages - currentExistingImages);

    const selectedFiles = Array.from(e.target.files);
    const filesToAdd = selectedFiles.slice(0, maxNewImages);

    if (filesToAdd.length < selectedFiles.length) {
      showNotification(`Only ${maxNewImages} more images can be added.`, "info");
    }

    // Compress each file
    const compressedFiles = await Promise.all(
      filesToAdd.map(async (file) => {
        const options = {
          maxSizeMB: 1,          // target max size
          maxWidthOrHeight: 1024, // resize image
          useWebWorker: true,
        };
        return await imageCompression(file, options);
      })
    );

    setFormData((prev) => ({
      ...prev,
      newImages: [...prev.newImages, ...compressedFiles],
    }));
  }
};

  const removeExistingImage = (index) => {
    const imageToRemove = formData.existingImages[index];
    
    if (imageToRemove) {
      setRemovedImages(prev => [...prev, imageToRemove]);
    }
    
    setFormData(prev => ({
      ...prev,
      existingImages: prev.existingImages?.filter((_, i) => i !== index) || []
    }));
  };

  const removeNewImage = (index) => {
    setFormData(prev => ({
      ...prev,
      newImages: prev.newImages.filter((_, i) => i !== index)
    }));
  };


const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);

  try {
    const formPayload = new FormData();
    
    formPayload.append('title', formData.title);
    formPayload.append('description', formData.description);
    formPayload.append('price', formData.price);
    formPayload.append('area', JSON.stringify(formData.area)); // Stringify area object
    formPayload.append('bathrooms', formData.bathrooms);
    formPayload.append('bedrooms', formData.bedrooms);
    formPayload.append('phone', formData.phone);
    formPayload.append('email', formData.email);
    formPayload.append('projectType', formData.projectType);
    formPayload.append('category', formData.category);
    
    formPayload.append('location', JSON.stringify(formData.location));
    formPayload.append('existingImages', JSON.stringify(formData.existingImages || []));
    formPayload.append('removedImages', JSON.stringify(removedImages));
    
    formData.newImages.forEach(image => {
      formPayload.append('images', image);
    });

      const token = localStorage.getItem("adminToken");
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      };

      if (project) {
        await axios.put(
          `https://gladious-constructions.onrender.com/api/properties/${project._id}`,
          formPayload,
          config
        );
      } else {
        await axios.post(
          'https://gladious-constructions.onrender.com/api/properties',
          formPayload,
          config
        );
      }
      
      showNotification(
        project ? 'Project updated successfully' : 'Project created successfully',
        'success'
      );
      onSave();
    } catch (err) {
      const errorMessage = err.response?.data?.error || 
                         err.message || 
                         'Failed to save project';
      
      showNotification(errorMessage);

      if (err.response?.status === 401) {
        localStorage.removeItem("adminToken");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div className="mp-modal-overlay">
      <div className="mp-project-modal">
        <Notification 
          show={notification.show} 
          message={notification.message} 
          type={notification.type}
          onClose={() => setNotification(prev => ({ ...prev, show: false }))}
        />
        
        <div className="mp-modal-header">
          <h2>{project ? 'Edit Project' : 'Create New Project'}</h2>
          <button className="mp-close-btn" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="mp-form-grid">
            <div className="mp-form-group">
              <label>Project Title*</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                required
                disabled={loading}
              />
            </div>
            
            <div className="mp-form-group">
              <label>Price (PKR)*</label>
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleChange}
                required
                min="0"
                disabled={loading}
              />
            </div>
            <div className="mp-form-group">
              <label>Phone Number</label>
              <input
                type="number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
                min="0"
                disabled={loading}
              />
            </div>
            <div className="mp-form-group">
              <label>Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                min="0"
                disabled={loading}
              />
            </div>
            
            <div className="mp-form-group">
              <label>Project Type</label>
              <select
                name="projectType"
                value={formData.projectType}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="for-sale">For Sale</option>
                <option value="for-rent">For Rent</option>
                <option value="upcoming">Upcoming</option>
                <option value="ongoing">Ongoing</option>
              </select>
            </div>
            
            <div className="mp-form-group">
              <label>Category</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                disabled={loading}
              >
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
              </select>
            </div>
            
          <div className="mp-form-group">
  <label>Area</label>
  <div className="mp-area-input-group">
    <input
      type="number"
      name="area.value"
      value={formData.area.value}
      onChange={handleChange}
      placeholder="Area value"
      min="0"
      disabled={loading}
      className="mp-area-value"
    />
    <select
      name="area.unit"
      value={formData.area.unit}
      onChange={handleChange}
      disabled={loading}
      className="mp-area-unit"
    >
      <option value="sqft">Sq Ft</option>
      <option value="sqm">Sq M</option>
      <option value="marla">Marla</option>
      <option value="kanal">Kanal</option>
      <option value="acre">Acres</option>
    </select>
  </div>
</div>
            
            <div className="mp-form-group">
              <label>Bedrooms</label>
              <input
                type="number"
                name="bedrooms"
                value={formData.bedrooms}
                onChange={handleChange}
                required
                min="0"
                disabled={loading}
              />
            </div>
            
            <div className="mp-form-group">
              <label>Bathrooms</label>
              <input
                type="number"
                name="bathrooms"
                value={formData.bathrooms}
                onChange={handleChange}
                required
                min="0"
                disabled={loading}
              />
            </div>
          </div>
          
          <div className="mp-form-group full-width">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              disabled={loading}
            />
          </div>
          
          <div className="mp-location-section">
            <h3>Location Details</h3>
            <div className="mp-form-grid">
              <div className="mp-form-group">
                <label>Address</label>
                <input
                  type="text"
                  name="location.address"
                  value={formData.location.address}
                  onChange={handleChange}
                  disabled={loading}
                />
              </div>
              
              <div className="mp-form-group">
                <label>City*</label>
                <input
                  type="text"
                  name="location.city"
                  value={formData.location.city}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
              
              <div className="mp-form-group">
                <label>Country*</label>
                <input
                  type="text"
                  name="location.country"
                  value={formData.location.country}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>
          </div>
          
          <div className="mp-image-section">
            <h3>Project Images</h3>
            <p className="mp-image-note">You can upload up to 5 images. Allowed formats: JPG, PNG, WEBP. <strong>Max file size: 1MB</strong></p>
            
            {formData.existingImages && formData.existingImages.length > 0 && (
              <div className="mp-existing-images">
                <h4>Current Images</h4>
                <div className="mp-image-preview-grid">
                  {formData.existingImages.map((image, index) => (
                    <div key={index} className="mp-image-preview-item">
                      <img 
                        src={`${image}`} 
                        alt={`Project ${index + 1}`} 
                        loading="lazy" 
                      />
                      <button 
                        type="button" 
                        className="mp-remove-image-btn"
                        onClick={() => removeExistingImage(index)}
                        disabled={loading}
                        title="Remove image"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {formData.newImages.length > 0 && (
              <div className="mp-new-images">
                <h4>New Images</h4>
                <div className="mp-image-preview-grid">
                  {formData.newImages.map((image, index) => (
                    <div key={index} className="mp-image-preview-item">
                      <img 
                        src={URL.createObjectURL(image)} 
                        alt={`New item ${index + 1}`} 
                      />
                      <button 
                        type="button" 
                        className="mp-remove-image-btn"
                        onClick={() => removeNewImage(index)}
                        disabled={loading}
                        title="Remove image"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="mp-form-group">
              <label>Upload Images</label>
              <input
                type="file"
                onChange={handleImageChange}
                multiple
                accept="image/jpeg,image/png,image/webp, image/jpg"
                className="mp-file-input"
                disabled={formData.existingImages.length + formData.newImages.length >= 5 || loading}
              />
              {(formData.newImages.length > 0 || (formData.existingImages?.length || 0) > 0) && (
                <div className="mp-file-input-label">
                  {formData.newImages.length > 0 && (
                    <span>{formData.newImages.length} file{formData.newImages.length > 1 ? 's' : ''} selected</span>
                  )}
                </div>
              )}
              {(formData.newImages.length + (formData.existingImages?.length || 0)) >= 5 && (
                <p className="mp-image-limit-warning">
                  Maximum number of images reached (5). 
                </p>
              )}
            </div>
          </div>
          
          <div className="mp-modal-actions">
            <button
              type="button"
              className="mp-cancel-btn"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="mp-save-btn"
              disabled={loading}
            >
              {loading ? 'Saving...' : project ? 'Save Changes' : 'Create Project'}
            </button>
          </div>
        </form>
      </div>
    </div>
 </> 
 );
};

export default ManageProjects;